import { motion } from "framer-motion";
import { Item } from "@/types/api";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { useGameStore } from "@/state/gameStore";
import { useCurrentRoom } from "@/state/selectors";
import { getItemAssetVariant } from "@/utils/itemUtils";
import { CoverCanvas } from "@/components/shared/CoverCanvas";
import { DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import sparkle from "@/assets/lottie/sparkle-burst.lottie";
import { getFormatKey } from "@/libs/assetPreloading/preloadConfig";
import { useCallback } from "react";

type Props = {
    item: Item;
    onClose: () => void;
};

const itemVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: { delay: 0.8, duration: 0.5, ease: "easeOut" },
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.2, ease: "easeIn" },
    },
} as const;

const DISPLAY_DURATION = 1.2; // seconds to show before auto-closing

const ItemAppearAnimation = ({ item, onClose }: Props) => {
    const roomVariant = useGameStore((state) => state.currentRoomVariant);
    const room = useCurrentRoom();

    const handleLottieRef = useCallback((instance: DotLottie | null) => {
        if (!instance) return;

        const onLoad = () => instance.play();

        instance.addEventListener("load", onLoad);

        if (instance.isLoaded) {
            onLoad();
        }
    }, []);

    if (!roomVariant) return null;

    const image = getItemAssetVariant(item, roomVariant);

    const format = room?.image.formats?.[getFormatKey("lg")];
    const imageW = format?.width ?? room?.image.width;
    const imageH = format?.height ?? room?.image.height;

    if (!imageW || !imageH) return null;

    // calculate sparkle position and scale depending on the rendered image dimeensios. The x and y values are relative
    // to the images original dimensions, so we need to scale them to the rendered dimensions.
    const sparkleLeft = (item.appearEffect?.offsetX || 0) * (imageW / (room?.image.width || imageW));
    const sparkleTop = (item.appearEffect?.offsetY || 0) * (imageH / (room?.image.height || imageH));
    const sparkleScale = (item.appearEffect?.scale || 1) * (imageW / (room?.image.width || imageW));

    const showSparkle = item.showEffect === null || item.showEffect === true;

    return (
        <div className="absolute inset-0 pointer-events-none z-20">
            <motion.div
                key={item.documentId}
                variants={itemVariants}
                initial="initial"
                animate="animate"
                onAnimationComplete={() => {
                    setTimeout(onClose, DISPLAY_DURATION * 1000);
                }}
                className="absolute inset-0"
            >
                <CoverCanvas imageW={imageW} imageH={imageH} className="absolute inset-0">
                    <StrapiMedia
                        size="lg"
                        image={image}
                        alt={image.url}
                        className="absolute inset-0 size-full"
                        objectFit="fill"
                        useImageAspectRatio={false}
                        priority
                    />

                    {showSparkle && (
                        <div
                            style={{
                                position: "absolute",
                                left: sparkleLeft,
                                top: sparkleTop,
                                width: 200,
                                height: 200,

                                transform: `translate(-50%, -50%) scale(${sparkleScale})`,
                                pointerEvents: "none",
                            }}
                        >
                            <DotLottieReact src={sparkle} loop={false} dotLottieRefCallback={handleLottieRef} />
                        </div>
                    )}
                </CoverCanvas>
            </motion.div>
        </div>
    );
};

export default ItemAppearAnimation;
